/**
 * LLD-XCC-210 / §24.2 rule 22 — the `a-onboard` wizard's own copy (stages 2–4,
 * design/12-onboarding-and-credentials.md §12.4–§12.6), sourced from Prototype_v2.html's
 * `ob-wz-1`…`ob-wz-6` panes. Split from `registry.ts` (the auth family's) because the two
 * families' screens never share a file.
 */

export const OB_WZ_1_COPY = {
  stepLabel: 'Credential',
  heading: 'Connect your Bitbucket credential',
  proseOne:
    'One credential type is supported: an Atlassian personal API token belonging to a Bitbucket workspace administrator. There is no other option to choose between.',
  proseTwo:
    'We ask for it before we ask which workspace to protect, so a failure here means one thing only: the credential is wrong. Bitbucket has no endpoint that lists the workspaces a token can see, so the next step asks you to type the workspace slug.',
  subheading: 'Three things to know before you start',
  step1Title: 'You create the token by hand, in Atlassian',
  step1Note: 'No API issues these tokens, so this cannot be automated. We open the right page with the scopes named.',
  step2Title: 'It must belong to a workspace admin',
  step2Note: 'Use a shared service account rather than an individual login, or backups break when that person changes role.',
  step3Title: 'A backup-only token still needs an admin scope',
  step3Note:
    'Reading branch permissions requires admin:repository:bitbucket and Bitbucket offers no read-only alternative. We never write during a backup, and every call is in your audit log.',
  openTokenPageLink: 'Open the Atlassian token page with the right scopes pre-selected →',
  cardHeading: 'Paste the token',
  tokenLabel: 'Token',
  tokenNote: 'A personal API token authenticates with Basic auth. It is stored encrypted, in a secret dedicated to this workspace.',
  nextButton: 'Next',
  footNote: 'The credential is the first step. Your organisation and your account already exist — they are created at sign-up, outside this wizard.',
} as const

export const OB_WZ_2_COPY = {
  stepLabel: 'Workspace',
  heading: 'Which workspace?',
  proseOne:
    'Enter your Bitbucket workspace slug. Bitbucket no longer offers any endpoint that lists the workspaces a token can see, so we cannot show you a list. We validate what you type and store the workspace’s permanent id, never the slug you typed. You can add others later from the workspace switcher.',
  proseTwo:
    'A mistyped slug is therefore a real failure mode. A 404 here names both possible causes and asks you to check the spelling first, then membership. A 401 is a different thing entirely — the credential, not the slug. Administrator rights are proven a moment later, on the permissions check.',
  cardHeading: 'Name the workspace',
  slugLabel: 'Bitbucket workspace slug',
  slugNote: 'The slug is the path segment in bitbucket.org/{slug}, not the workspace display name.',
  adminWarning: 'The token must belong to a workspace admin. We cannot verify that here — the next screen checks it.',
  backButton: 'Back',
  nextButton: 'Next',
} as const

export const OB_WZ_3_COPY = {
  stepLabel: 'Region',
  heading: 'Where should this workspace’s backups live?',
  proseOneTemplate: (slug: string) => `This choice decides the storage bucket, the encryption key and the credential store used for ${slug}.`,
  proseTwo:
    'It is made once and it is permanent. It is asked here, in the flow, rather than offered later as a setting you can edit — because a one-time irreversible choice presented as an editable dropdown produces an entirely predictable support conversation.',
  proseThree: 'Each workspace carries its own region. An organisation may hold workspaces in both.',
  cardHeading: 'Region',
  permanentWarning:
    'This cannot be changed afterwards. Moving a workspace’s backups to another region means disconnecting it and starting a new baseline — the stored objects, the key that encrypts them and the secret that holds the credential are all regional.',
  residencyNote: 'No artifact is written outside the region you choose. Bitbucket Cloud offers no residency control of its own.',
  confirmTemplate: (slug: string) => `I understand this choice is permanent for ${slug}.`,
  backButton: 'Back',
  connectButton: 'Connect workspace',
} as const

export const OB_WZ_4_COPY = {
  stepLabel: 'Scope check',
  heading: 'Check what this token is allowed to read',
  proseTemplate: (slug: string) =>
    `Six probes, run against ${slug} before any backup starts. A missing scope found here is a checklist item; the same gap found on day nine of a first backup is a restart. Every probe is listed before any of them runs, so you can see the whole extent of what we ask for.`,
  panelHeading: 'Scope check',
  notRunLabel: 'Not run',
  recheckAllButton: 'Recheck all',
  timeoutNote: 'Whole run times out after 60 seconds',
  footNote:
    'Continue is enabled when every required probe is green. An unverified required scope blocks: a rate-limited probe has told us nothing about the token, so it is never rendered as a missing permission.',
  backButton: '‹ Back to the credential',
  continueButton: 'Continue',
  wrongTokenKindNote: 'This token was minted without Bitbucket scopes. Create a new one using "Create API token with scopes" and enter it again.',
  requiredLabel: 'Required',
  optionalLabel: 'Optional',
  acknowledgeGapsPrompt: 'Continue without these optional capabilities?',
  acknowledgeButton: 'Continue anyway',
} as const

export const OB_WZ_5_COPY = {
  stepLabel: 'Repositories',
  heading: 'Which repositories are in scope?',
  proseOne: 'Everything is included by default. Excluding a repository means it is never read and is reported as excluded, never as failed.',
  proseTwo:
    'You can change this at any time. A repository added to scope later is treated as a first-time full capture for that repository alone, while everything else stays incremental.',
  cardHeading: 'Scope',
  allRepositoriesTemplate: (count: number) => `All ${count} repositories`,
  chooseNowOption: 'Choose repositories now',
  // Was 'Choosing individual repositories is not built in this pass — see the frontend/README.md
  // finding.' — a literal internal dev-note string, in customer-facing copy (found live, Sanket,
  // 2026-09-25). This whole pane is currently out of the active wizard flow (state.ts's own doc
  // comment), so this string has no live caller today, but it is fixed here too rather than left
  // as a landmine for whoever re-enables the pane.
  chooseNowUnavailableNote: 'Choosing individual repositories now is not available yet — every repository is included by default.',
  autoProtectLabel: 'Automatically protect new repositories?',
  autoProtectYes: 'Yes — protect everything by default (recommended)',
  autoProtectNo: 'No — I’ll add new repositories myself',
  autoProtectNote:
    'Answered once, changeable later, and every change is written to the audit log. Set to no, a new repository lands in a Not protected list on the dashboard with a count badge until you add it.',
  backButton: 'Back',
  nextButton: 'Next',
} as const

export const OB_WZ_6_COPY = {
  stepLabel: 'First backup',
  heading: 'Start your first backup',
  subheading: 'Read this before you start — it sets expectations that matter.',
  progressiveNote: 'Your first backup is a progressive process, not a single job. Repositories become restorable as they land, so you do not wait for the whole thing to finish before you are protected.',
  whyTitle: 'Why it takes time',
  whyNote:
    'Your code itself is fast — it arrives over git. The slow part is pull request history, available only through an API capped at 1,000 requests an hour on a personal API token. That metadata is the whole of the remaining time.',
  configBackupNote:
    'This starts both your data backup and your configuration backup — repository and workspace settings, permissions, pipelines and deployment history included. The two are decoupled, each on its own independent schedule, so neither one waits on the other.',
  startButton: 'Start my first backup',
  skipLink: 'Skip for now — run it tonight on the schedule',
} as const
